import { Lightbox, MediaContainer } from '@design-system';
import classNames from 'classnames';
import React, { useMemo } from 'react';

import { TrackMultiMediaClick } from '../../../utils/analytics';
import { storyblokMediaItemToLightboxEntry } from '../../../utils/lightbox';
import {
  isStoryblokRichTextPresent,
  renderStoryblokRichText,
} from '../../../utils/renderStoryblokRichText';
import { Image } from '../../Image';

type MultiMediaGalleryProps = {
  config: MultiMediaCarousel;
  showCaption?: boolean;
  analytics?: Analytics;
  eagerLoading?: boolean;
};

const MultiMediaGallery = ({
  analytics,
  config,
  eagerLoading,
  showCaption = true,
}: MultiMediaGalleryProps) => {
  const lightboxItems = useMemo(
    () => config.items.map(storyblokMediaItemToLightboxEntry),
    [config.items],
  );

  return (
    <Lightbox items={lightboxItems}>
      {(lightbox) => (
        <div className="grid grid-cols-2 gap-3">
          {config.items.map((mediaItem, index) => {
            return (
              <div className={index === 0 ? 'col-span-2' : 'col-span-1'}>
                {mediaItem.component === 'image' && (
                  <div
                    className="group text-left"
                    onClick={() => {
                      lightbox.openOnSlide(index);
                    }}
                    onKeyDown={() => {
                      lightbox.openOnSlide(index);
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <MediaContainer
                      mediaComponent={
                        <Image
                          alt={mediaItem.image.alt}
                          eagerLoading={eagerLoading}
                          focus={mediaItem.image.focus}
                          hoverEffect
                          shape="rounded"
                          size={{
                            sm: {
                              ratio: index === 0 ? '3/2' : '1/1',
                              span: 12,
                            },
                            md: { ratio: index === 0 ? '3/2' : '1/1', span: 4 },
                            lg: { ratio: index === 0 ? '3/2' : '1/1', span: 3 },
                          }}
                          src={mediaItem.image.filename}
                        />
                      }
                      type="Image"
                    />
                    {showCaption &&
                      mediaItem.caption &&
                      isStoryblokRichTextPresent(mediaItem.caption) && (
                        <div className="mt-3 block text-neutral-4">
                          {renderStoryblokRichText(mediaItem.caption)}
                        </div>
                      )}
                  </div>
                )}

                {mediaItem.component === 'Video' && (
                  <div
                    className={classNames(
                      'group text-left',
                      index === 0 ? 'col-span-2' : 'col-span-1',
                    )}
                    onClick={() => {
                      lightbox.openOnSlide(index);
                      if (analytics) {
                        TrackMultiMediaClick({
                          page: analytics.page,
                          context: analytics.context,
                          type: 'video',
                          durationInSeconds:
                            mediaItem?.vimeo?.vimeo_oembed?.response.duration,
                          title: mediaItem.title
                            ? mediaItem.title
                            : mediaItem?.vimeo?.vimeo_oembed?.response.title,
                          position: index,
                        });
                      }
                    }}
                    onKeyDown={() => {
                      lightbox.openOnSlide(index);
                      if (analytics) {
                        TrackMultiMediaClick({
                          page: analytics.page,
                          context: analytics.context,
                          type: 'video',
                          durationInSeconds:
                            mediaItem?.vimeo?.vimeo_oembed?.response.duration,
                          title: mediaItem.title
                            ? mediaItem.title
                            : mediaItem?.vimeo?.vimeo_oembed?.response.title,
                          position: index,
                        });
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <MediaContainer
                      duration={mediaItem.vimeo.vimeo_oembed.response.duration}
                      mediaComponent={
                        <Image
                          alt={mediaItem.coverImage?.alt}
                          eagerLoading={eagerLoading}
                          hoverEffect
                          shape="rounded"
                          size={{
                            sm: {
                              ratio: index === 0 ? '3/2' : '1/1',
                              span: 12,
                            },
                            md: { ratio: index === 0 ? '3/2' : '1/1', span: 4 },
                            lg: { ratio: index === 0 ? '3/2' : '1/1', span: 3 },
                          }}
                          src={mediaItem.coverImage?.filename}
                        />
                      }
                      title={mediaItem.title}
                      type="Video"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Lightbox>
  );
};

export default MultiMediaGallery;
